import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@/lib/router";
import { usePractice } from "../context/PracticeContext";
import { metricAlertsApi } from "../api/metricAlerts";
import { analyticsStudioApi } from "../api/analyticsStudio";
import { authApi } from "../api/auth";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "@/components/ui/button";
import { WidgetChart } from "../components/charts/WidgetChart";
import {
  Sparkles,
  ArrowRight,
  Check,
  Clock,
  AlertTriangle,
  CircleAlert,
  Info,
} from "lucide-react";
import { cn } from "../lib/utils";

function greetingFor(date = new Date()): string {
  const hr = date.getHours();
  if (hr < 5) return "Hello";
  if (hr < 12) return "Good morning";
  if (hr < 18) return "Good afternoon";
  return "Good evening";
}

function formatLongDate(date = new Date()): string {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function SeverityPill({ severity }: { severity: string }) {
  const map: Record<string, { Icon: typeof Info; label: string; classes: string }> = {
    critical: {
      Icon: CircleAlert,
      label: "Critical",
      classes: "bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-800/40",
    },
    warning: {
      Icon: AlertTriangle,
      label: "Heads up",
      classes: "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800/40",
    },
    info: {
      Icon: Info,
      label: "FYI",
      classes: "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800/40",
    },
  };
  const spec = map[severity] ?? map.info;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium",
        spec.classes,
      )}
    >
      <spec.Icon className="h-3 w-3" />
      {spec.label}
    </span>
  );
}

interface KpiTileProps {
  label: string;
  value: string;
  hint?: string;
}

function KpiTile({ label, value, hint }: KpiTileProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 flex flex-col min-w-0">
      <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">
        {label}
      </div>
      <div className="text-3xl font-bold tracking-tight mt-2 truncate">
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-muted-foreground mt-1 truncate">{hint}</div>
      )}
    </div>
  );
}

function MetricKpi({ companyId, metric }: { companyId: string; metric: { key: string; label: string; query: { model: string; explore: string; fields: string[]; filters?: Record<string, string>; sorts?: string[]; limit?: number } } }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["home-kpi", companyId, metric.key],
    queryFn: () => analyticsStudioApi.runDirect(companyId, metric.query),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <KpiTile label={metric.label} value="…" />;
  if (error || !data) return <KpiTile label={metric.label} value="—" hint="couldn't load" />;

  // Pick the last numeric value from the first row (single_value tile convention).
  const first = data.rows?.[0];
  let val: string = "—";
  if (first) {
    const numeric = Object.values(first).reverse().find(
      (v) => typeof v === "number",
    );
    if (typeof numeric === "number") {
      val = numeric.toLocaleString();
    } else {
      const last = Object.values(first).pop();
      if (last !== undefined && last !== null) val = String(last);
    }
  }
  return <KpiTile label={metric.label} value={val} />;
}

export function Home() {
  const navigate = useNavigate();
  const { selectedPracticeId } = usePractice();

  const { data: session } = useQuery({
    queryKey: queryKeys.auth.session,
    queryFn: () => authApi.getSession(),
    retry: false,
  });

  const userFirstName = useMemo(() => {
    const full = session?.user?.name ?? session?.user?.email ?? "there";
    return full.split(/\s|@/)[0] || "there";
  }, [session]);

  // Active alerts (top 5 unacknowledged, ordered by severity desc via API)
  const { data: alerts, refetch: refetchAlerts } = useQuery({
    queryKey: ["home-alerts", selectedPracticeId],
    queryFn: () =>
      metricAlertsApi.list(selectedPracticeId!, { acknowledged: false, limit: 5 }),
    enabled: !!selectedPracticeId,
    refetchInterval: 60_000,
  });

  // Front-page metric catalog (the 4 KPIs we tile on Home)
  const { data: catalog } = useQuery({
    queryKey: ["available-metrics-front-page", selectedPracticeId],
    queryFn: () => metricAlertsApi.availableMetrics(selectedPracticeId!),
    enabled: !!selectedPracticeId,
    staleTime: 5 * 60 * 1000,
  });
  const frontPageMetrics = useMemo(() => {
    return (catalog ?? []).filter((m) => (m as { frontPage?: boolean }).frontPage).slice(0, 4);
  }, [catalog]);

  async function ack(alertId: string) {
    if (!selectedPracticeId) return;
    await metricAlertsApi.acknowledge(selectedPracticeId, alertId);
    void refetchAlerts();
  }

  // Snooze = hide locally for 24h (distinct from Done, which acknowledges
  // server-side). Stored per practice in localStorage as { [alertId]: untilMs }.
  const snoozeKey = `ligament.snoozedAlerts.${selectedPracticeId ?? "none"}`;
  const [snoozedTick, setSnoozedTick] = useState(0);
  const snoozedUntil = useMemo<Record<string, number>>(() => {
    void snoozedTick; // re-read after a snooze
    try {
      return JSON.parse(localStorage.getItem(snoozeKey) ?? "{}");
    } catch {
      return {};
    }
  }, [snoozeKey, snoozedTick]);
  function snooze(alertId: string) {
    const now = Date.now();
    const pruned = Object.fromEntries(
      Object.entries(snoozedUntil).filter(([, until]) => until > now),
    );
    const next = { ...pruned, [alertId]: now + 24 * 60 * 60 * 1000 };
    localStorage.setItem(snoozeKey, JSON.stringify(next));
    setSnoozedTick((t) => t + 1);
  }
  const visibleAlerts = useMemo(
    () => (alerts ?? []).filter((a) => (snoozedUntil[a.id] ?? 0) < Date.now()),
    [alerts, snoozedUntil],
  );

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto space-y-8">
      {/* ── Greeting ─────────────────────────────────────────── */}
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">
          {greetingFor()}, {userFirstName}
        </h1>
        <p className="text-sm text-muted-foreground">{formatLongDate()}</p>
        {visibleAlerts.length > 0 && (
          <p className="pt-3 text-sm flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
            <span>
              <span className="font-medium">{visibleAlerts.length}</span>{" "}
              {visibleAlerts.length === 1 ? "thing needs" : "things need"} your attention
            </span>
          </p>
        )}
      </header>

      {/* ── Needs attention ─────────────────────────────────── */}
      {visibleAlerts.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            Needs attention
          </h2>
          <div className="space-y-2">
            {visibleAlerts.map((a) => (
              <article
                key={a.id}
                className="rounded-xl border border-border bg-card p-5 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <SeverityPill severity={a.severity} />
                  <span className="text-[11px] text-muted-foreground ml-auto">
                    {new Date(a.createdAt).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <h3 className="text-base font-semibold leading-tight">
                  {a.thresholdLabel}
                </h3>
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    Now{" "}
                    <span className="font-mono text-foreground">
                      {typeof a.currentValue === "number"
                        ? a.currentValue.toLocaleString()
                        : a.currentValue}
                    </span>
                  </span>
                  <span>
                    Target{" "}
                    <span className="font-mono text-foreground">
                      {a.operator} {a.thresholdValue}
                    </span>
                  </span>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed">
                  {a.message}
                </p>
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate("/analytics")}
                  >
                    Open
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => ack(a.id)}>
                    <Check className="h-3.5 w-3.5 mr-1.5" />
                    Done
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => snooze(a.id)}
                    title="Hide for 24 hours (stays open server-side)"
                  >
                    <Clock className="h-3.5 w-3.5 mr-1.5" />
                    Snooze
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {alerts && visibleAlerts.length === 0 && (
        <section className="rounded-xl border border-border bg-card p-6 text-center">
          <Check className="h-6 w-6 mx-auto text-emerald-500 mb-2" />
          <h2 className="text-base font-semibold">All clear</h2>
          <p className="text-sm text-muted-foreground mt-1">
            No active alerts. Nothing needs your attention right now.
          </p>
        </section>
      )}

      {/* ── Today at a glance ───────────────────────────────── */}
      {frontPageMetrics.length > 0 && selectedPracticeId && (
        <section className="space-y-2">
          <h2 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            Today
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {frontPageMetrics.map((m) => (
              <MetricKpi
                key={m.key}
                companyId={selectedPracticeId}
                metric={m}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Ask anything ────────────────────────────────────── */}
      <section
        className="rounded-2xl border border-border bg-card p-6 cursor-pointer hover:border-foreground/30 transition-colors"
        onClick={() => navigate("/ask")}
      >
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-accent/40 p-3">
            <Sparkles className="h-5 w-5 text-foreground/80" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold">Ask anything</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              In plain English. Get a chart and a short read-out of what to do.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {[
                "How are leads tracking this week?",
                "Which clinic has the most no-shows?",
                "Compare reviews to last month",
              ].map((q) => (
                <span
                  key={q}
                  className="text-xs rounded-full bg-accent/30 hover:bg-accent/50 px-3 py-1.5 text-muted-foreground"
                >
                  {q}
                </span>
              ))}
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground self-center" />
        </div>
      </section>
    </div>
  );
}
